// Fieldlink blog — static article store. Each post targets a specific NZ
// civil-construction search keyword cluster. Add new posts to the top.
// Block types: h2, p, ul (items), quote.
export const BLOG_POSTS = [
  {
    slug: "job-management-software-vs-spreadsheets-nz",
    title: "Job Management Software vs Spreadsheets: What It's Really Costing Your NZ Crew",
    description:
      "Spreadsheets feel free — but for a NZ civil or trades crew they quietly cost 5–10 office hours a week. Here's the honest comparison, with real numbers.",
    date: "2026-08-27",
    readMins: 6,
    tag: "Job management",
    blocks: [
      { t: "p", c: "Every NZ contractor starts on spreadsheets. A jobs tab, a timesheet tab, maybe a whiteboard for dispatch. It works — right up until the third crew, the fourth ute and the fiftieth job number. This is what the spreadsheet is actually costing you, and when it's time to move to proper job management software." },
      { t: "h2", c: "The hidden hours" },
      { t: "ul", c: [
        "Re-typing paper timesheets into the pay run: 2–4 hours a week",
        "Chasing missing hours and dockets on Friday afternoon: 1–2 hours",
        "Matching supplier invoices to jobs from memory: 1–2 hours",
        "Working out what a job actually cost after it's finished: an afternoon per job — usually skipped",
      ] },
      { t: "p", c: "Call it 5–10 office hours a week. At $40–$60/hour that's $800–$2,400 a month of admin — quietly more than any software subscription in the country." },
      { t: "h2", c: "What the spreadsheet can never tell you" },
      { t: "p", c: "The bigger cost isn't hours — it's the questions a spreadsheet can't answer while the job is still running: What has J0042 cost so far? Are we still inside the quote? Which jobs this year actually made money? Job management software answers those live, because every timesheet, plant hour, material and supplier invoice files itself against the job number as it happens." },
      { t: "h2", c: "When to switch" },
      {"t": "link", "c": "Plan the move around a real job:", "to": "/job-management-nz", "label": "a practical guide to managing jobs with Fieldlink"},
      { t: "ul", c: [
        "You have 2+ crews on different sites on the same day",
        "The office spends Friday chasing paper instead of invoicing",
        "You've had a job lose money and only found out at year-end",
        "A main contractor is asking for SSSPs and QA records you keep in a ring binder",
      ] },
      { t: "quote", c: "The spreadsheet is the most expensive job management system a growing crew can run — you just pay for it in hours instead of dollars." },
      { t: "p", c: "Fieldlink is NZ-made job management software with flat NZD pricing — jobs, dispatch, timesheets, SSSPs, fleet and live cost-per-job in one app. Start a 7-day free trial and run one real job through it alongside the spreadsheet. The comparison makes itself." },
    ],
  },
  {
    slug: "how-to-price-civil-jobs-nz",
    title: "How to Price Civil Jobs in NZ (And Stop Losing Money on the 'Easy' Ones)",
    description:
      "A practical pricing guide for NZ civil contractors: real cost rates for labour and plant, overheads per week, margin targets, and how live back-costing keeps quotes honest.",
    date: "2026-08-27",
    readMins: 7,
    tag: "Job costing",
    blocks: [
      { t: "p", c: "Most civil jobs in New Zealand don't lose money on the dig — they lose it in the quote. Guessed plant rates, forgotten overheads and 'she'll be right' margins mean the easy-looking jobs are often the worst earners. Here's a straightforward way to price civil work that holds up." },
      { t: "h2", c: "Start with your true hourly cost — not your charge-out rate" },
      { t: "p", c: "A digger op on $35/hour costs you closer to $45 once you add ACC, KiwiSaver, leave and downtime. A 13-tonne excavator costs $60–$90/hour to own and run before it turns a blade. These figures are indicative only — actual costs vary with location, ground conditions, access, plant age and market conditions. Price from your true cost, not from what the bloke down the road charges." },
      { t: "h2", c: "Overheads: the silent margin-killer" },
      { t: "p", c: "Yard rent, insurance, phones, software, the ute fleet's WOFs — fixed overheads for a small civil crew commonly run $2,000–$5,000 a week. Divide your weekly overhead by your billable hours and add it to every quoted hour. If you don't, every job is subsidising the yard." },
      { t: "h2", c: "A simple pricing checklist" },
      { t: "ul", c: [
        "Labour: true cost per hour × estimated hours × 1.1 for the stuff-around factor",
        "Plant: internal charge-out rate per machine-hour, including float time",
        "Materials + cartage: quoted supply rates plus 5–10% wastage",
        "Overhead recovery: your per-billable-hour overhead share",
        "Margin: 15–25% on top — margin is profit, not a buffer for mistakes",
      ] },
      { t: "h2", c: "The feedback loop: back-costing" },
      { t: "link", c: "Put that review against the actual job records:", to: "/civil-job-costing-software-nz", label: "civil job costing in Fieldlink" },
      {"t": "link", "c": "Connect the estimate to what happens on site:", "to": "/job-management-nz", "label": "follow the job from planning to cost review"},
      { t: "p", c: "Pricing only improves if you compare quote vs actual on every job. That's brutal by hand — and automatic with job management software. Fieldlink builds each job's live cost from timesheets, plant run-hours, materials and supplier invoices, and shows the margin percentage next to the spend while the job is still running. Bad pricing shows up in week one, not at year-end." },
      { t: "quote", c: "The contractors who price best aren't guessing better — they're the ones who see quote vs actual on every single job." },
    ],
  },
  {
    slug: "timesheet-record-keeping-requirements-nz",
    title: "Timesheet & Wage Record Requirements in NZ: What Contractors Must Keep",
    description:
      "What the Employment Relations Act and Holidays Act require NZ employers to record for hours and wages, how long to keep it, and how a timesheet app keeps you audit-ready.",
    date: "2026-08-27",
    readMins: 6,
    tag: "Compliance",
    blocks: [
      { t: "p", c: "If a Labour Inspector knocks, 'the hours are in the group chat' is not a wages and time record. NZ employment law is specific about what you must keep for every employee — and paper timesheets in a ute glovebox rarely survive contact with an audit. Here's what's required and the easiest way to stay covered." },
      { t: "h2", c: "What you must record" },
      { t: "ul", c: [
        "Hours worked each day in each pay period, and the pay for those hours",
        "Wages paid each pay period and how they were calculated",
        "Leave taken and leave balances — annual, sick, bereavement, public holidays",
        "Details of employment agreements and any changes to hours",
      ] },
      { t: "p", c: "Records generally need to be kept for at least six years, and you must be able to produce them if MBIE asks. Penalties for missing or inadequate records run to five figures — and in a pay dispute, no records means the employee's version of the hours usually wins." },
      { t: "h2", c: "Why paper and spreadsheets fail audits" },
      { t: "p", c: "Paper gets lost, spreadsheets get edited. Neither shows when hours were logged, who approved them, or how the pay was calculated from them. An auditor wants a chain: hours logged → approved → paid, with dates and names attached at each step." },
      { t: "h2", c: "What a timesheet app gives you" },
      { t: "ul", c: [
        "Every entry stamped with who logged it, when, and against which job",
        "An approval trail — who reviewed it, when, and any rejection reasons",
        "Automatic leave balance updates when sick or annual days are approved",
        "Overtime calculated by rule, not by memory, and visible on the pay run",
        "Six-plus years of records retrievable in seconds, not shoeboxes",
      ] },
      { t: "p", c: "Fieldlink keeps all of this automatically for NZ crews — hours from the field, approvals in the office, overtime rules applied, leave balances live, and a one-click export to Xero Payroll NZ. Audit-ready by default, not by Friday-night panic." },
    ],
  },
  {
    slug: "how-much-does-job-management-software-cost-nz",
    title: "How Much Does Job Management Software Cost in NZ? (2026 Price Guide)",
    description:
      "Real 2026 NZD pricing for job management software in New Zealand — Simpro, Tradify, Fergus and Fieldlink compared for a typical 10-person civil crew.",
    date: "2026-06-08",
    pricingNote: true,
    readMins: 6,
    tag: "Pricing",
    blocks: [
      { t: "p", c: "If you run a civil or trades crew in New Zealand, software pricing is a minefield: AUD price tags, per-user billing, onboarding fees and 12-month lock-ins. Here's what job management software actually costs a Kiwi crew in 2026 — in NZD, all-in." },
      { t: "h2", c: "The per-user trap" },
      { t: "p", c: "Most big platforms bill per user, per month. That sounds cheap at $30–$60/user until you do the maths on a 10-person crew: $300–$600/month before add-ons. Then GPS tracking, forms and integrations get sold as extras." },
      { t: "h2", c: "What the main options cost a 10-person NZ crew" },
      { t: "ul", c: [
        "Simpro: roughly NZ$800–$1,200/month once you include the modules a civil crew actually needs, plus a four-figure onboarding fee.",
        "Tradify: from ~NZ$45/user/month — but it's built for solo tradies, not multi-crew dispatch.",
        "Fergus: ~NZ$40–$60/user/month, trades-focused, weaker on fleet and H&S.",
        "Fieldlink: from NZ$39.99/month flat for the whole crew (up to 12 people) — timesheets, dispatch, SSSPs, fleet and payroll export included.",
      ] },
      { t: "h2", c: "Hidden costs to ask about before you sign" },
      { t: "ul", c: [
        "Onboarding / setup fees (often $1,000+ on enterprise platforms)",
        "Per-user charges for field workers who only submit timesheets",
        "GPS / tracking sold as a paid add-on",
        "Charges for API access or Xero integration",
        "12-month contracts with no exit clause",
      ] },
      { t: "h2", c: "The real question: cost per job, not cost per month" },
      {"t": "link", "c": "Compare the workflow as well as the subscription:", "to": "/job-management-nz", "label": "job management for NZ civil and construction teams"},
      { t: "p", c: "A $99/month tool that saves the office 5 hours a week on timesheets and back-costing pays for itself before morning smoko on day one. Compare software on hours saved, not sticker price." },
      { t: "quote", c: "A 10-person crew switching from a per-user platform to a flat-rate NZ tool typically saves NZ$8,000–$10,000 a year in subscription fees alone." },
      { t: "p", c: "Fieldlink is NZ-made, priced in NZD, and every plan includes every feature. Start a free trial — no card, no onboarding fee, no lock-in." },
    ],
  },
  {
    "slug": "what-is-an-sssp-complete-nz-guide",
    "title": "What Is an SSSP? Site-Specific Plans for NZ Civil Work",
    "description": "An introduction to site-specific safety plans: project information, site hazards, document review and keeping the plan available to the crew.",
    "date": "2026-05-26",
    "readMins": 4,
    "tag": "Health & Safety",
    "blocks": [
      {
        "t": "p",
        "c": "A Site-Specific Safety Plan (SSSP) brings together information about the work, the site, identified hazards, planned controls and emergency arrangements. Its value depends on how well it reflects the actual site and how the team uses and reviews it."
      },
      {
        "t": "h2",
        "c": "Start with the actual scope"
      },
      {
        "t": "p",
        "c": "A rural culvert replacement and a subdivision drainage job have different work areas, interfaces and access arrangements. Use the project information and your business process to decide what belongs in the plan. Copying a site name into a generic document does not check the rest of its content."
      },
      {
        "t": "h2",
        "c": "Information to consider"
      },
      {
        "t": "ul",
        "c": [
          "Project, scope, location and relevant contacts",
          "Identified hazards and the controls planned for the work",
          "Emergency information and communication arrangements",
          "Plant, PPE and task-specific competency information",
          "How people will access the plan and how changes will be communicated"
        ]
      },
      {
        "t": "p",
        "c": "This is an introductory outline, not an exhaustive legal or contractual checklist. Confirm the requirements for your project with the appropriate people."
      },
      {
        "t": "h2",
        "c": "Keep the current plan identifiable"
      },
      {
        "t": "p",
        "c": "Review the plan when the work or site conditions change. Make clear which document people should use and how updated information will reach them. A signature records an acknowledgement; it does not independently establish understanding or site safety."
      },
      {
        "t": "h2",
        "c": "Drafting and document access in Fieldlink"
      },
      {
        "t": "p",
        "c": "Fieldlink has a structured SSSP editor with AI-assisted drafting and PDF export. People review the generated content. A separate uploaded-document workflow keeps an existing SSSP with a job and records worker sign-off."
      },
      {
        "t": "p",
        "c": "The generated-plan editor has draft and lock controls; it is not a full published revision-history system. Keep your document issue and change process explicit."
      },
      {
        "t": "link",
        "c": "For the product workflow, see",
        "to": "/sssp-generator-nz",
        "label": "SSSP drafting and job documents"
      },
      {
        "t": "link",
        "c": "For related site records, explore",
        "to": "/safety-management-nz",
        "label": "Fieldlink Health & Safety"
      }
    ]
  },
  {
    slug: "best-timesheet-app-for-nz-construction-crews",
    title: "The Best Timesheet App for NZ Construction Crews in 2026",
    description:
      "What to look for in a construction timesheet app in New Zealand: offline mode, job codes, Holidays Act compliance and Xero export — plus how the options stack up.",
    date: "2026-05-12",
    readMins: 6,
    tag: "Timesheets",
    blocks: [
      { t: "p", c: "Paper timesheets cost NZ construction firms real money: hours get rounded up, job codes get scribbled wrong, and the office spends every Monday deciphering handwriting. Here's what actually matters when choosing a timesheet app for a Kiwi crew." },
      { t: "h2", c: "1. It has to work with no signal" },
      { t: "p", c: "Half of NZ's civil work happens where there's no reception — rural roading, forestry blocks, new subdivisions. If the app can't record time offline and sync later, your crew is back to texting hours to the office. Fieldlink is a PWA with full offline support: enter hours in a dead zone, it syncs when you're back in coverage." },
      { t: "h2", c: "2. Hours must land against a job, not just a day" },
      { t: "link", c: "For the product workflow, see", to: "/timesheet-app-nz", label: "construction timesheets with separate labour and plant records" },
      { t: "p", c: "Total hours tells you what to pay. Hours per job tells you whether you made money. Every timesheet entry should carry a job number so back-costing happens automatically instead of in a spreadsheet at year end." },
      { t: "h2", c: "3. Holidays Act compliance is non-negotiable" },
      { t: "p", c: "New Zealand's Holidays Act 2003 is famously hard to get right — time-and-a-half plus alternative days for public holidays worked, otherwise ordinary daily pay. Your timesheet app should calculate this, not leave it to a formula in Excel." },
      { t: "h2", c: "4. Payroll export that your accountant doesn't hate" },
      {"t": "link", "c": "See where site hours fit into the wider operation:", "to": "/job-management-nz", "label": "connect timesheets with the job record"},
      { t: "p", c: "The Friday pay run should be: pick the week, approve, export. Fieldlink produces a Xero / MYOB-ready CSV with rates and Holidays Act loadings already applied." },
      { t: "h2", c: "What a 10-person crew saves" },
      { t: "ul", c: [
        "Office admin: 4–6 hours/week no longer spent chasing and re-keying paper sheets",
        "Leakage: 10–15 min/day/worker of rounded-up time disappears",
        "Disputes: GPS-stamped start/finish times end the 'I was there at 7' arguments",
      ] },
      { t: "p", c: "Fieldlink's timesheet app is NZ-made, works offline, links every hour to a job, and exports straight to payroll. Free 7-day trial for the whole crew." },
    ],
  },
  {
    slug: "holidays-act-payroll-guide-nz-civil-contractors",
    title: "Holidays Act Payroll for NZ Civil Contractors: A Plain-English Guide",
    description:
      "Public holidays, time-and-a-half, alternative days and ordinary daily pay — how the NZ Holidays Act 2003 applies to construction crews, explained without the legalese.",
    date: "2026-04-28",
    readMins: 8,
    tag: "Payroll",
    blocks: [
      { t: "p", c: "The Holidays Act 2003 has caught out some of New Zealand's biggest employers — remediation bills in the tens of millions. For a civil contractor with variable hours, weekend work and wet-weather days, the risk is real. Here's the plain-English version of what you must get right." },
      { t: "h2", c: "Working a public holiday: the two-part rule" },
      { t: "ul", c: [
        "Pay at least time-and-a-half for the hours actually worked on the public holiday.",
        "If the day was an 'otherwise working day' for that employee, they also get a full paid alternative day (day in lieu).",
      ] },
      { t: "h2", c: "What is an 'otherwise working day'?" },
      { t: "p", c: "It's whether the employee would normally have worked that day if it weren't a holiday. For crews with regular Monday–Friday patterns it's easy. For casual and variable rosters you look at the work pattern over the recent weeks — this is where paper records fall apart and digital timesheets save you." },
      { t: "h2", c: "Not working the public holiday" },
      { t: "p", c: "If it's an otherwise working day and the employee doesn't work, they're paid their relevant daily pay or average daily pay for the day — not just left unpaid." },
      { t: "h2", c: "The traps that catch construction firms" },
      { t: "ul", c: [
        "Averaging pay over the wrong period when hours vary week to week",
        "Missing alternative days for workers on rotating or weekend rosters",
        "Regular overtime and allowances excluded from holiday pay calculations",
        "No records proving which days were 'otherwise working days'",
      ] },
      { t: "h2", c: "How software keeps you clean" },
      { t: "p", c: "Accurate, timestamped, per-day records are the foundation of Holidays Act compliance. Fieldlink's pay run takes the crew's actual recorded hours, applies time-and-a-half and flags alternative days for public holidays, then exports the result to Xero or MYOB — with the audit trail attached." },
      { t: "quote", c: "MBIE's own guidance says most Holidays Act failures trace back to poor records, not bad intent." },
      { t: "p", c: "This guide is general information, not legal advice — for edge cases talk to your payroll provider or employment lawyer." },
    ],
  },
  {
    "slug": "pre-start-checklists-nz-machine-inspections-guide",
    "title": "Plant Pre-Start Records in NZ: From Checks to Follow-Up",
    "description": "Keep plant pre-start responses, defect notes and follow-up records together. Understand the difference between a machine check and a site briefing.",
    "date": "2026-04-14",
    "readMins": 4,
    "tag": "Fleet & Plant",
    "blocks": [
      {
        "t": "p",
        "c": "A plant pre-start record captures what an operator checked and reported before using a machine. A useful record identifies the equipment, the person, the date, the responses and any defects that need follow-up."
      },
      {
        "t": "h2",
        "c": "Plant checks and site briefings are different"
      },
      {
        "t": "p",
        "c": "The machine record concerns a particular plant item. A site pre-start or briefing concerns the work, crew and site conditions. Keep those contexts clear so the office can retrieve the right information."
      },
      {
        "t": "h2",
        "c": "Choose checks for the machine"
      },
      {
        "t": "p",
        "c": "Use your company process and the relevant manufacturer information to set the checks. Generic examples might include fluids, tyres or tracks, attachments, restraint systems, visibility equipment, alarms and visible damage. This is not an operating instruction or a complete inspection standard."
      },
      {
        "t": "h2",
        "c": "Keep defects and follow-up together"
      },
      {
        "t": "p",
        "c": "Record the observed issue and supporting information, then follow your company process for escalation and decisions about use. A checked box, photo or signature is a record; it is not an independent assessment that a machine is safe."
      },
      {
        "t": "h2",
        "c": "How Fieldlink stores the record"
      },
      {
        "t": "p",
        "c": "The operator selects the machine and completes the company checklist with notes, photos, the applicable meter reading and a signature. Submitted records can be reviewed in the office. Failed items designated as mechanical create damage reports against that machine."
      },
      {
        "t": "p",
        "c": "An overall unsafe response sets the fleet record to maintenance pending office review. This software status is not physical isolation or automatic authorisation to return the machine to service. Plan for connectivity and confirm that the record has saved."
      },
      {
        "t": "link",
        "c": "See the software workflow:",
        "to": "/pre-start-checklist-app-nz",
        "label": "plant pre-start records in Fieldlink"
      },
      {
        "t": "link",
        "c": "For a printable starting point:",
        "to": "/templates/plant-inspection-template",
        "label": "plant inspection template"
      },
      {
        "t": "link",
        "c": "For job-linked forms and site records:",
        "to": "/safety-management-nz",
        "label": "Health & Safety workspace"
      }
    ]
  },
  {
    slug: "xero-integration-for-nz-construction-companies",
    title: "Xero for NZ Construction Companies: Connecting the Field to Your Accounts",
    description:
      "How NZ construction and civil firms connect field data — timesheets, supplier invoices and job costs — to Xero, and what a good integration should automate.",
    date: "2026-03-30",
    readMins: 6,
    tag: "Xero & Accounts",
    blocks: [
      { t: "p", c: "Most NZ construction firms run Xero. Very few connect it to what actually happens in the field — so the office re-keys timesheets into payroll, codes supplier invoices by hand, and finds out a job lost money three months after it finished. Here's what field-to-Xero integration should look like in 2026." },
      { t: "h2", c: "1. Timesheets → payroll without re-keying" },
      { t: "p", c: "The crew's approved hours — with rates and Holidays Act loadings applied — should flow to Xero as a ready-to-run pay item file. Fieldlink's Friday pay run does this in three clicks: pick the week, approve, export." },
      { t: "h2", c: "2. Supplier invoices coded to jobs automatically" },
      { t: "p", c: "The PlaceMakers slip your foreman grabs at the counter has a job number written on it. Fieldlink's AI scanner reads the photo, finds the job number, records the cost against the job and matches it when the monthly statement lands in Xero. No more shoebox of receipts." },
      { t: "h2", c: "3. Customer invoices tracked, not lost" },
      { t: "p", c: "Progress claims and cartage invoices raised from job data sync to Xero as draft invoices, then Fieldlink watches payment status — so overdue payments show up on your dashboard, not in a year-end surprise." },
      { t: "h2", c: "4. Real back-costing" },
      {"t": "link", "c": "Bring the field records and office review together:", "to": "/job-management-nz", "label": "explore the connected job workspace"},
      { t: "ul", c: [
        "Labour: hours × cost rate from timesheets",
        "Plant: machine hours × internal charge rate",
        "Materials: scanned supplier invoices",
        "Result: live cost-vs-quote on every job, matching what Xero sees",
      ] },
      { t: "h2", c: "What to check before connecting any tool to Xero" },
      { t: "ul", c: [
        "Certified Xero API integration (OAuth, not password sharing)",
        "You control which invoices sync and when",
        "Mapping to your existing chart of accounts and tracking categories",
        "NZ GST handled correctly on every line",
      ] },
      { t: "p", c: "Fieldlink connects to Xero in about two minutes from Settings. Start a free trial and see your first pay run land in Xero this Friday." },
    ],
  },
];

export function getPost(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

export function formatDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", {
    day: "numeric", month: "long", year: "numeric",
  });
}
